// Phase 3A — Visitor Journey model.
//
// Pure, deterministic, dependency-free. No personalization beyond what the
// caller already knows (auth state, membership state, last content kind the
// visitor opened). Nothing here reads storage or network; every consumer
// passes context in, which keeps the model testable and SSR-safe.
//
// Behavioral architecture: LISTEN → ENTER → OWN.
//   LISTEN  low-friction public value (music, wisdom, activations preview)
//   ENTER   deeper relationship (free account, First Gate, newsletter, Academy)
//   OWN     direct ownership (music/books/beats purchase, membership)
//
// This is a hierarchy and a recommendation model, never a forced funnel.

export const JOURNEY_STAGES = ["listen", "enter", "own"] as const;
export type JourneyStage = (typeof JOURNEY_STAGES)[number];

export const VISITOR_STATES = [
  "anonymous-first-visit",
  "returning-anonymous",
  "free-account",
  "member",
] as const;
export type VisitorState = (typeof VISITOR_STATES)[number];

/** Content surfaces that carry a deliberate next-step ladder. */
export type JourneyKind =
  | "home"
  | "music"
  | "wisdom"
  | "gate"
  | "activation"
  | "book"
  | "academy"
  | "shop";

export interface JourneyContext {
  /** Supabase session present. */
  signedIn: boolean;
  /** Active paid membership (never inferred from anything else). */
  isMember: boolean;
  /** True only when a previous visit is known from first-party storage. */
  returning: boolean;
  /** Deterministic, optional deep-link targets supplied by the caller. */
  releaseSlug?: string;
  gateN?: number;
  /** Structured relationship hints (codex_links etc). Never journal content. */
  relatedConceptSlug?: string;
}

export interface JourneyAction {
  /** Stable id used for analytics; never contains user data. */
  id: string;
  label: string;
  /** Route path. Callers using dynamic segments pass `params`. */
  to: string;
  params?: Record<string, string>;
  stage: JourneyStage;
  /** One short line of honest context. No urgency, no pressure. */
  note?: string;
}

export function resolveVisitorState(ctx: {
  signedIn: boolean;
  isMember: boolean;
  returning: boolean;
}): VisitorState {
  if (ctx.isMember) return "member";
  if (ctx.signedIn) return "free-account";
  return ctx.returning ? "returning-anonymous" : "anonymous-first-visit";
}

/**
 * The stage a visitor is *invited into* next. Membership does not end the
 * journey — members stay in "own" because their next best actions are deeper
 * access, not another sign-up ask.
 */
export function stageForVisitor(state: VisitorState): JourneyStage {
  switch (state) {
    case "anonymous-first-visit":
      return "listen";
    case "returning-anonymous":
      return "enter";
    case "free-account":
      return "enter";
    case "member":
      return "own";
  }
}

const LISTEN_MUSIC = (slug?: string): JourneyAction =>
  slug
    ? {
        id: "listen_release",
        label: "Listen",
        to: "/music/$slug",
        params: { slug },
        stage: "listen",
        note: "Start with the sound.",
      }
    : { id: "listen_music", label: "Listen", to: "/music", stage: "listen", note: "Start with the sound." };

const ENTER_FIRST_GATE: JourneyAction = {
  id: "enter_first_gate",
  label: "Begin at the First Gate",
  to: "/gates/$n",
  params: { n: "1" },
  stage: "enter",
  note: "A quiet practice, at your own pace.",
};

const ENTER_ACTIVATION: JourneyAction = {
  id: "enter_activation",
  label: "Today's activation",
  to: "/activations",
  stage: "enter",
  note: "Breath, intention, instruction.",
};

const ENTER_ACCOUNT: JourneyAction = {
  id: "enter_account",
  label: "Create a free account",
  to: "/auth",
  stage: "enter",
  note: "Keep your place across the ecosystem.",
};

const OWN_MEMBERSHIP: JourneyAction = {
  id: "own_membership",
  label: "See membership",
  to: "/membership",
  stage: "own",
  note: "Deeper access — never a wall on first value.",
};

const OWN_DASHBOARD: JourneyAction = {
  id: "own_dashboard",
  label: "Your dashboard",
  to: "/dashboard",
  stage: "own",
  note: "Everything you have access to.",
};

/**
 * Exactly one primary action per visitor state. Callers must render this as
 * the single dominant CTA above the fold.
 */
export function primaryAction(state: VisitorState, ctx: Partial<JourneyContext> = {}): JourneyAction {
  switch (state) {
    case "anonymous-first-visit":
      return LISTEN_MUSIC(ctx.releaseSlug);
    case "returning-anonymous":
      return ENTER_FIRST_GATE;
    case "free-account":
      return ctx.gateN && ctx.gateN > 1
        ? {
            id: "continue_gate",
            label: `Continue at Gate ${ctx.gateN}`,
            to: "/gates/$n",
            params: { n: String(ctx.gateN) },
            stage: "enter",
            note: "Where you left off.",
          }
        : ENTER_FIRST_GATE;
    case "member":
      return OWN_DASHBOARD;
  }
}

/** At most two secondary actions. Never duplicates the primary. */
export function secondaryActions(
  state: VisitorState,
  ctx: Partial<JourneyContext> = {},
): JourneyAction[] {
  const list: JourneyAction[] = (() => {
    switch (state) {
      case "anonymous-first-visit":
        return [ENTER_FIRST_GATE, ENTER_ACTIVATION];
      case "returning-anonymous":
        return [LISTEN_MUSIC(ctx.releaseSlug), ENTER_ACCOUNT];
      case "free-account":
        return [ENTER_ACTIVATION, OWN_MEMBERSHIP];
      case "member":
        return [ENTER_ACTIVATION, LISTEN_MUSIC(ctx.releaseSlug)];
    }
  })();
  const primary = primaryAction(state, ctx);
  return list.filter((a) => a.id !== primary.id).slice(0, 2);
}

/**
 * Deliberate next-step ladder per content type. Returns at most three steps,
 * ordered by journey hierarchy. Relationship data (codex concept) is used when
 * the caller supplies it, rather than hard-coding topical links.
 */
export function nextSteps(
  kind: JourneyKind,
  state: VisitorState,
  ctx: Partial<JourneyContext> = {},
): JourneyAction[] {
  const concept: JourneyAction | null = ctx.relatedConceptSlug
    ? {
        id: "related_concept",
        label: "The concept behind it",
        to: "/codex/$slug",
        params: { slug: ctx.relatedConceptSlug },
        stage: "listen",
        note: "How this connects in the Codex.",
      }
    : null;

  const nextGate: JourneyAction | null =
    typeof ctx.gateN === "number" && ctx.gateN < 47
      ? {
          id: "next_gate",
          label: `Gate ${ctx.gateN + 1}`,
          to: "/gates/$n",
          params: { n: String(ctx.gateN + 1) },
          stage: "enter",
          note: "The next gate on the map.",
        }
      : null;

  const base: Array<JourneyAction | null> = (() => {
    switch (kind) {
      case "music":
        return [concept, ENTER_ACTIVATION, state === "member" ? OWN_DASHBOARD : ENTER_ACCOUNT];
      case "wisdom":
        return [concept, ENTER_FIRST_GATE, ENTER_ACTIVATION];
      case "gate":
        return [
          nextGate,
          { id: "gate_journal", label: "Write in your journal", to: "/journey/journal", stage: "enter" },
          ENTER_ACTIVATION,
        ];
      case "activation":
        return [
          { id: "activation_archive", label: "Continue the practice", to: "/activations/archive", stage: "enter" },
          ENTER_FIRST_GATE,
          state === "member" ? OWN_DASHBOARD : OWN_MEMBERSHIP,
        ];
      case "book":
        return [
          { id: "book_library", label: "Read in your library", to: "/library", stage: "own" },
          concept,
          OWN_MEMBERSHIP,
        ];
      case "academy":
        return [
          { id: "academy_continue", label: "Continue in the Academy", to: "/academy", stage: "enter" },
          ENTER_ACTIVATION,
          state === "member" ? OWN_DASHBOARD : OWN_MEMBERSHIP,
        ];
      case "shop":
        return [
          { id: "shop_library", label: "Your library", to: "/library", stage: "own" },
          LISTEN_MUSIC(ctx.releaseSlug),
          OWN_MEMBERSHIP,
        ];
      case "home":
      default:
        return [primaryAction(state, ctx), ...secondaryActions(state, ctx)];
    }
  })();

  const seen = new Set<string>();
  const out: JourneyAction[] = [];
  for (const a of base) {
    if (!a) continue;
    // Never ask a member to buy membership again; never ask a signed-in
    // visitor to create an account. No invented state, no repeated asks.
    if (a.id === "own_membership" && state === "member") continue;
    if (a.id === "enter_account" && (state === "free-account" || state === "member")) continue;
    if (seen.has(a.id)) continue;
    seen.add(a.id);
    out.push(a);
  }
  return out.slice(0, 3);
}
